import React, { useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/20/solid';

export default function RealEstateSection({ data, onChange, onAddProperty, onRemoveProperty, isOpen, onOpen }) {
  const [collapsed, setCollapsed] = useState(false);
  const isMarried = data.maritalStatus === 'married' && data.spouseFiling === 'yes';

  const handlePropertyChange = (index, field, value, isSpouse = false) => {
    const key = isSpouse ? "spouseOtherRealEstateProperties" : "otherRealEstateProperties";
    const updated = [...data[key]];
    updated[index] = { ...updated[index], [field]: value };
    onChange({ target: { name: key, value: updated } });
  };
  const renderMortgageCard = (property, index, isSpouse = false, isSecond = false) => {
    const prefix = isSecond ? "secondMortgage" : "mortgage";
    return (
      <div className="bg-white p-4 rounded shadow-xl border">
        <h4 className="text-sm font-medium text-gray-800 mb-2">{isSecond ? "Second Mortgage" : "First Mortgage"}</h4>
        <input
          placeholder="Creditor"
          value={property[`${prefix}Creditor`] || ""}
          onChange={(e) => handlePropertyChange(index, `${prefix}Creditor`, e.target.value, isSpouse)}
          className="input mb-2"
        />
        <div className="grid grid-cols-2 gap-3">
          <input
            placeholder="Loan Balance"
            value={property[`${prefix}Balance`] || ""}
            onChange={(e) => handlePropertyChange(index, `${prefix}Balance`, e.target.value, isSpouse)}
            className="input"
          />
          <input
            placeholder="Monthly Payment"
            value={property[`${prefix}MonthlyPayment`] || ""}
            onChange={(e) => handlePropertyChange(index, `${prefix}MonthlyPayment`, e.target.value, isSpouse)}
            className="input"
          />
        </div>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <select
            value={property[`${prefix}PaymentStatus`] || ""}
            onChange={(e) => handlePropertyChange(index, `${prefix}PaymentStatus`, e.target.value, isSpouse)}
            className="input"
          >
            <option value="">Are you current?</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
          {property[`${prefix}PaymentStatus`] === "no" && (
            <input
              placeholder="Months Behind"
              value={property[`${prefix}MonthsBehind`] || ""}
              onChange={(e) => handlePropertyChange(index, `${prefix}MonthsBehind`, e.target.value, isSpouse)}
              className="input"
            />
          )}
        </div>
      </div>
    );
  };

  const renderPropertyCard = (property, index, isSpouse = false) => (
    <div key={index} className="space-y-4 bg-gray-50 border rounded p-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Address Section */}
        <div className="bg-white p-4 rounded shadow-xl border">
          <h4 className="text-sm font-medium text-gray-800 mb-2">Property Address</h4>
          <input
            placeholder="Street"
            value={property.street || ""}
            onChange={(e) => handlePropertyChange(index, "street", e.target.value, isSpouse)}
            className="input mb-2"
          />
          <input
            placeholder="Street 2"
            value={property.street2 || ""}
            onChange={(e) => handlePropertyChange(index, "street2", e.target.value, isSpouse)}
            className="input mb-2"
          />
          <div className="grid grid-cols-[65%_10%_21%] gap-2">
            <input
              placeholder="City"
              value={property.city || ""}
              onChange={(e) => handlePropertyChange(index, "city", e.target.value, isSpouse)}
              className="input"
            />
            <input
              placeholder="ST"
              value={property.state || ""}
              onChange={(e) => handlePropertyChange(index, "state", e.target.value, isSpouse)}
              className="input"
            />
            <input
              placeholder="ZIP"
              value={property.zip || ""}
              onChange={(e) => handlePropertyChange(index, "zip", e.target.value, isSpouse)}
              className="input"
            />
          </div>
        </div>

        {/* Ownership + Mortgage */}
        <div className="bg-white p-4 rounded shadow-xl border">
          <h4 className="text-sm font-medium text-gray-800 mb-2">Ownership Details</h4>
          <input
            placeholder="Property Value"
            value={property.homeValue || ""}
            onChange={(e) => handlePropertyChange(index, "homeValue", e.target.value, isSpouse)}
            className="input mb-2"
          />
          <select
            value={property.propertyType || ""}
            onChange={(e) => handlePropertyChange(index, "propertyType", e.target.value, isSpouse)}
            className="input mb-2"
          > 
            <option value="">Property Type</option>
            <option value="single_family">Single-Family Home</option>
            <option value="duplex">Duplex</option>
            <option value="condo">Condominium</option>
            <option value="manufactured">Manufactured Home</option>
            <option value="land">Land</option>
            <option value="investment">Investment Property</option>
            <option value="timeshare">Timeshare</option>
            <option value="other">Other</option>
          </select>
          <select
            value={property.ownership || ""}
            onChange={(e) => handlePropertyChange(index, "ownership", e.target.value, isSpouse)}
            className="input mb-2"
          >
            <option value="">Ownership</option>
            <option value="TxE">Tenancy by the Entireties</option>
            <option value="FS">Fee Simple</option>
            <option value="joint">Joint</option>
            <option value="LE">Life Estate</option>
            <option value="remainder">Remainder</option>
          </select>
          <select
            value={property.hasMortgage || ""}
            onChange={(e) => handlePropertyChange(index, "hasMortgage", e.target.value, isSpouse)}
            className="input"
          >
            <option value="">Has a Mortgage?</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </div>
      </div>

      {/* Mortgage Details if applicable */}
      {property.hasMortgage === "yes" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {renderMortgageCard(property, index, isSpouse, false)}
          {renderMortgageCard(property, index, isSpouse, true)}
        </div>
      )}

      {/* Remove Button */}
      <button
        type="button"
        onClick={() => onRemoveProperty(index, isSpouse)}
        className="text-red-500 text-sm mt-2"
      >
        Remove Property
      </button>
    </div>
  );

  return (
    
    <section className="space-y-4">
      <div className="flex items-center justify-between bg-blue-100 px-4 py-2 rounded shadow-xl cursor-pointer" onClick={onOpen}>
        <h2 className="text-lg font-semibold text-gray-800">Real Property</h2>
        {isOpen ? <ChevronRightIcon className="w-5 h-5" /> : <ChevronDownIcon className="w-5 h-5" />}
      </div>

      {isOpen && (
          <>
          {/* PRINCIPAL RESIDENCE */}
          <div className="bg-white shadow-xl rounded border p-4">
            <h3 className="text-md font-semibold text-gray-700 mb-4">Principal Residence</h3>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <select
                name="ownsHome"
                value={data.ownsHome}
                onChange={onChange}
                className="input mb-2"
              >
                <option value="">Do you own your home?</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
              {data.ownsHome === "yes" && (
                <>
                  <input
                    type="text"
                    name="homeValue"
                    value={data.homeValue}
                    onChange={onChange}
                    placeholder="Home Value"
                    className="input mb-2"
                  />
                  <select
                  name='homePropertyType'
                    value={data.homePropertyType || ""}
                    onChange={onChange}
                    className="input mb-2"
                  > 
                    <option value="">Property Type</option>
                    <option value="single_family">Single-Family Home</option>
                    <option value="duplex">Duplex</option>
                    <option value="condo">Condominium</option>
                    <option value="manufactured">Manufactured Home</option>
                    <option value="land">Land</option>
                    <option value="investment">Investment Property</option>
                    <option value="timeshare">Timeshare</option>
                    <option value="other">Other</option>
                  </select>
                  <select
                    name="homeOwnershipType"
                    value={data.homeOwnershipType}
                    onChange={onChange}
                    className="input mb-2"
                  >
                    <option value="">Ownership</option>
                    <option value="TxE">Tenancy by the Entireties</option>
                    <option value="FS">Fee Simple</option>
                    <option value="FS-spouse">Fee Simple - Spouse</option>
                    <option value="joint">Joint</option>
                    <option value="LE">Life Estate</option>
                    <option value="remainder">Remainder</option>
                  </select>
                  <select
                    name="hasMortgage"
                    value={data.hasMortgage}
                    onChange={onChange}
                    className="input mb-2"
                  >
                    <option value="">Has Mortgage?</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </>
              )}
            </div>
          </div>
          {data.ownsHome === "yes" && data.hasMortgage === "yes" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              {/* First Mortgage */}
              <div className="bg-white p-4 rounded shadow-xl border">
                <h4 className="text-sm font-medium text-gray-800 mb-2">First Mortgage</h4>
                <input
                  placeholder="Creditor"
                  name="mortgageCreditor"
                  value={data.mortgageCreditor}
                  onChange={onChange}
                  className="input mb-2"
                />
                <div className="grid grid-cols-2 gap-3">
                  <input
                    placeholder="Loan Balance"
                    name="mortgageBalance"
                    value={data.mortgageBalance}
                    onChange={onChange}
                    className="input"
                  />
                  <input
                    placeholder="Monthly Payment"
                    name="mortgageMonthlyPayment"
                    value={data.mortgageMonthlyPayment}
                    onChange={onChange}
                    className="input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <select
                    name="mortgagePaymentStatus"
                    value={data.mortgagePaymentStatus}
                    onChange={onChange}
                    className="input"
                  >
                    <option value="">Are you current?</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                  {data.mortgagePaymentStatus === "no" && (
                    <input
                      name="mortgageMonthsBehind"
                      value={data.mortgageMonthsBehind}
                      onChange={onChange}
                      placeholder="Months Behind"
                      className="input"
                    />
                  )}
                </div>
              </div>

              {/* Second Mortgage */}
              <div className="bg-white p-4 rounded shadow-xl border">
                <h4 className="text-sm font-medium text-gray-800 mb-2">Second Mortgage</h4>
                <input
                  placeholder="Creditor"
                  name="secondMortgageCreditor"
                  value={data.secondMortgageCreditor}
                  onChange={onChange}
                  className="input mb-2"
                />
                <div className="grid grid-cols-2 gap-3">
                  <input
                    placeholder="Loan Balance"
                    name="secondMortgageBalance"
                    value={data.secondMortgageBalance}
                    onChange={onChange}
                    className="input"
                  />
                  <input
                    placeholder="Monthly Payment"
                    name="secondMortgageMonthlyPayment"
                    value={data.secondMortgageMonthlyPayment}
                    onChange={onChange}
                    className="input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <select
                    name="secondMortgagePaymentStatus"
                    value={data.secondMortgagePaymentStatus}
                    onChange={onChange}
                    className="input"
                  >
                    <option value="">Are you current?</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                  {data.secondMortgagePaymentStatus === "no" && (
                    <input
                      name="secondMortgageMonthsBehind"
                      value={data.secondMortgageMonthsBehind}
                      onChange={onChange}
                      placeholder="Months Behind"
                      className="input"
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        <div className="space-y-6">
          {/* Other Real Estate */}
          <div>
            <h3 className="text-md font-semibold">Other Real Estate</h3>
            {data.otherRealEstateProperties?.map((prop, idx) => renderPropertyCard(prop, idx))}
            <button
              type="button"
              onClick={() => onAddProperty(false)}
              className="mt-3 text-blue-600 text-sm"
            >
              + Add Property
            </button>
          </div>

          {/* Spouse Real Estate */}
          {isMarried && (
            <div>
              <h3 className="text-md font-semibold">Spouse Real Estate</h3>
              {data.spouseOtherRealEstateProperties?.map((prop, idx) => renderPropertyCard(prop, idx, true))}
              <button
                type="button"
                onClick={() => onAddProperty(true)}
                className="mt-3 text-blue-600 text-sm"
              >
                + Add Spouse Property
              </button>
            </div>
          )}
        </div>
        </>
      )}
    </section>
  );
}